import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Edit, Trash2, FileText, CheckCircle, 
  Package, Calendar, AlertTriangle, BookOpen, Layers,
  CheckSquare, School, Printer
} from 'lucide-react'
import { spkService } from '../services/spkService'

const SPKDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [spk, setSpk] = useState(null)
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    loadSPK()
  }, [id])

  const loadSPK = async () => {
    try {
      const response = await spkService.getById(id)
      if (response.success) {
        setSpk(response.data.spk)
      }
    } catch (error) {
      console.error('Error loading SPK:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmComplete = async () => {
    if (!window.confirm(`Selesaikan SPK "${spk.judul}"? Status SPK akan berubah menjadi COMPLETED.`)) return
    setConfirming(true)
    try {
      const response = await spkService.confirmCompletion(id)
      if (response.success) {
        loadSPK() // reload to update state
      } else {
        alert(response.message || 'Gagal menyelesaikan SPK')
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message))
    } finally {
      setConfirming(false)
    }
  }

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      normal: 'bg-blue-100 text-blue-700 border-blue-200',
      low: 'bg-gray-100 text-gray-700 border-gray-200',
    }
    return styles[priority] || styles.normal
  }

  const getStatusBadge = (status) => {
    const styles = {
      in_progress: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-gray-100 text-gray-700',
    }
    return styles[status] || styles.pending
  }

  const getJenjangBadge = (jenjang) => {
    const styles = {
      SD: 'bg-green-500 text-white',
      SMP: 'bg-blue-500 text-white',
      SMA: 'bg-purple-500 text-white',
    }
    return styles[jenjang] || 'bg-gray-500 text-white'
  }

  // Progress Bar Component
  const ProgressBar = ({ label, progress, color }) => (
    <div className="flex items-center gap-3">
      <div className="w-16 text-sm font-medium text-gray-600">{label}</div>
      <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="w-12 text-sm text-gray-600 text-right">{progress}%</div>
      {progress === 100 && (
        <CheckCircle className="w-5 h-5 text-green-500" />
      )}
    </div>
  )

  // File Checklist Item - 3 states: validated (green), pending (yellow), rejected (red)
  const FileCheckItem = ({ label, file }) => {
    const status = file?.status
    const version = file?.version || 0

    let style = 'bg-gray-100 text-gray-500'
    let iconStyle = 'text-gray-400'

    if (status === 'validated') {
      style = 'bg-green-100 text-green-700'
      iconStyle = 'text-green-600'
    } else if (status === 'pending') {
      style = 'bg-yellow-100 text-yellow-700'
      iconStyle = 'text-yellow-600'
    } else if (status === 'rejected') {
      style = 'bg-red-100 text-red-700'
      iconStyle = 'text-red-600'
    }

    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${style}`}>
        <CheckSquare className={`w-4 h-4 ${iconStyle}`} />
        <span>{label}</span>
        {version > 1 && <span className="text-xs opacity-70">v{version}</span>}
        {status && (
          <span className="text-xs opacity-70 uppercase">{status}</span>
        )}
      </div>
    )
  }

  // Batch Item Component
  const BatchItem = ({ batch, type }) => (
    <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
      type === 'reguler' ? 'bg-gray-700 text-white' : 'bg-purple-600 text-white'
    }`}>
      {batch.batch_number}: {batch.qty} eks → {batch.station}
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!spk) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">SPK tidak ditemukan</p>
        <Link to="/spk" className="text-blue-600 hover:underline mt-2 inline-block">
          Kembali ke daftar SPK
        </Link>
      </div>
    )
  }

  const totalBatchQty = spk.batches?.reduce((sum, b) => sum + (b.qty || 0), 0) || 0
  const completedBatchQty = spk.batches?.filter(b => b.station === 'done').reduce((sum, b) => sum + (b.qty || 0), 0) || 0
  const progressPercent = spk.qty > 0 ? Math.round((completedBatchQty / spk.qty) * 100) : 0

  const regulerBatches = spk.batches?.filter(b => b.batch_type === 'reguler') || []
  const customBatches = spk.batches?.filter(b => b.batch_type === 'custom') || []

  // Check if all batches are in warehouse (done)
  const allBatchesDone = spk.batches?.length > 0 && spk.batches.every(b => b.station === 'done' || b.station === 'gudang')
  const canComplete = allBatchesDone && spk.status === 'in_progress' && !spk.manager_confirmed

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link 
        to="/spk" 
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800"
      >
        <ArrowLeft className="w-4 h-4" />
        Kembali
      </Link>

      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Top badges */}
        <div className="px-6 pt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${getPriorityBadge(spk.priority)}`}>
              {spk.priority}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(spk.status)}`}>
              {spk.status?.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <span className={`px-3 py-1 rounded-lg text-sm font-bold ${getJenjangBadge(spk.jenjang)}`}>
            {spk.jenjang}
          </span>
        </div>

        {/* Title */}
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-800">{spk.judul}</h1>
          <div className="text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>{spk.id}</span>
            <span>•</span>
            <span>{spk.kode}</span>
            <span>•</span>
            <span>{spk.qty} eks</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Deadline: {new Date(spk.deadline).toLocaleDateString('id-ID')}
            </span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="px-6 py-3 bg-gray-50 flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <span className="text-gray-600">Order SPK:</span>
            <span className="font-semibold">{spk.qty} eks</span>
          </div>
          <div className="flex items-center gap-2">
            <Printer className="w-4 h-4 text-green-600" />
            <span className="text-gray-600">Cetak Isi:</span>
            <span className="font-semibold">{spk.qty} eks</span>
          </div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-red-600" />
            <span className="text-gray-600">Cetak Cover:</span>
            <span className="font-semibold">{spk.qty - (spk.customSchools?.reduce((s, c) => s + c.qty, 0) || 0)} eks</span>
          </div>
        </div>

        {/* Progress Bars */}
        <div className="px-6 py-4 space-y-3">
          <ProgressBar 
            label="Isi" 
            progress={spk.progress_isi || 0} 
            color="bg-blue-500" 
          />
          <ProgressBar 
            label="Cover" 
            progress={spk.progress_cover || 0} 
            color="bg-red-500" 
          />
          <ProgressBar 
            label="Main" 
            progress={progressPercent} 
            color="bg-purple-500" 
          />
        </div>

        {/* Custom Schools */}
        {spk.customSchools && spk.customSchools.length > 0 && (
          <div className="px-6 py-3 bg-yellow-50 border-t flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
            <span className="text-yellow-700 font-medium">
              {spk.customSchools.length} Sekolah Custom:
            </span>
            <span className="text-yellow-600">
              {spk.customSchools.map(cs => cs.school_code).join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Rekap & Batches */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rekap Produksi */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              Rekap Produksi Selesai
            </h2>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-600">{completedBatchQty}</div>
              <div className="text-sm text-gray-500">dari {spk.qty} target ({progressPercent}%)</div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-gray-700">Reguler</span>
                <span className="text-gray-500 text-sm">({regulerBatches.length} batch)</span>
              </div>
              <span className="font-semibold">
                {regulerBatches.filter(b => b.station === 'done').reduce((s, b) => s + b.qty, 0)} eks
              </span>
            </div>

            {spk.customSchools?.map((cs, index) => {
              const csBatches = customBatches.filter(b => b.school_code === cs.school_code)
              const csComplete = csBatches.filter(b => b.station === 'done').reduce((s, b) => s + b.qty, 0)
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <School className="w-4 h-4 text-purple-600" />
                    <span className="text-gray-700">{cs.school_name}</span>
                    <span className="text-gray-500 text-sm">({csBatches.length} batch)</span>
                  </div>
                  <span className="font-semibold">{csComplete} eks</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Batch Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5 text-purple-600" />
            Info Batch
          </h2>

          {/* Reguler Batches */}
          {regulerBatches.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="font-medium text-gray-700">Reguler: {regulerBatches.length} Batch</span>
              </div>
              <div className="space-y-2">
                {regulerBatches.map((batch, index) => (
                  <BatchItem key={index} batch={batch} type="reguler" />
                ))}
              </div>
            </div>
          )}

          {/* Custom Batches per School */}
          {spk.customSchools?.map((cs, index) => {
            const csBatches = customBatches.filter(b => b.school_code === cs.school_code)
            if (csBatches.length === 0) return null
            return (
              <div key={index} className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <School className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-gray-700">Batch per Sekolah Custom</span>
                </div>
                <div className="text-sm text-purple-600 mb-2">{cs.school_name}</div>
                <div className="space-y-2">
                  {csBatches.map((batch, idx) => (
                    <BatchItem key={idx} batch={batch} type="custom" />
                  ))}
                </div>
              </div>
            )
          })}

          {regulerBatches.length === 0 && customBatches.length === 0 && (
            <div className="text-center text-gray-400 py-8">
              Belum ada batch
            </div>
          )}

          {/* Complete Button */}
          {canComplete && (
            <button
              onClick={handleConfirmComplete}
              disabled={confirming}
              className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-5 h-5" />
              {confirming ? 'Memproses...' : 'Selesaikan SPK'}
            </button>
          )}
        </div>
      </div>

      {/* File Checklist */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">File Status</h2>
        <div className="flex flex-wrap gap-3">
          <FileCheckItem
            label="File ISI"
            file={spk.files?.find(f => f.file_type === 'isi' && !f.school_code)}
          />
          <FileCheckItem
            label="File Cover"
            file={spk.files?.find(f => f.file_type === 'cover' && !f.school_code)}
          />
          {spk.customSchools?.map((cs, index) => (
            <FileCheckItem
              key={index}
              label={`Cover ${cs.school_code}`}
              file={spk.files?.find(f => f.file_type === 'cover_custom' && f.school_code === cs.school_code)}
            />
          ))}
          {spk.customSchools?.filter(cs => cs.custom_type === 'cover_sisipan').map((cs, index) => (
            <FileCheckItem
              key={`sisipan-${index}`}
              label={`Sisipan ${cs.school_code}`}
              file={spk.files?.find(f => f.file_type === 'sisipan' && f.school_code === cs.school_code)}
            />
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex flex-wrap gap-4 justify-center">
          <button className="px-6 py-2 bg-gray-100 rounded-lg text-gray-700 font-medium hover:bg-gray-200 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Detail
          </button>
          <Link 
            to={`/spk/${spk.id}/edit`}
            className="px-6 py-2 bg-blue-100 rounded-lg text-blue-700 font-medium hover:bg-blue-200 flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Link>
          <button className="px-6 py-2 bg-red-100 rounded-lg text-red-700 font-medium hover:bg-red-200 flex items-center gap-2">
            <Trash2 className="w-4 h-4" />
            Hapus
          </button>
        </div>
      </div>
    </div>
  )
}

export default SPKDetailPage
